import type {
  BakedBlockInstance,
  BakedBlockPlacement,
  BlockDefinition,
  BlockDefinitionMeta,
  BlockVersionMeta,
  BorderEdge,
  PrintRotation,
  RegionBorders,
  Template,
  Zone,
} from '@tsagko/label-contracts'
import { getPrinterProfile, getPrinterProfiles } from '@tsagko/label-printer-profiles'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAdapter } from '../adapters/AdapterProvider'
import { useUnsavedGuard } from '../state/useUnsavedGuard'
import { Canvas } from '../canvas/Canvas'
import { useCanvasDeselect } from '../canvas/useCanvasDeselect'
import { useResolvedImages } from '../canvas/useResolvedImages'
import { CanvasControls } from '../canvas/CanvasControls'
import { computeZoneLayout, type LaidOutZone } from '../canvas/layout'
import { fitsInMm } from '../canvas/fit-mm'
import { formatMm, formatMmCeil, formatMmFloor } from '../canvas/format-mm'
import { Ruler } from '../canvas/Ruler'
import { RulerDimensionInput } from '../canvas/RulerDimensionInput'
import { DimensionLockButton } from '../canvas/DimensionLockButton'
import { PrintRotationControl } from '../canvas/PrintRotationControl'
import { clampZoom, fitZoomToViewport } from '../canvas/transforms'
import { findCoincidentEdge } from '../canvas/border-ops'
import {
  findZoneByPath,
  replaceZoneAtPath,
  resolveBoxAtPath,
  setZoneBordersAtPath,
  zoneIdAtPath,
} from '../canvas/tree-ops'
import {
  hasContent,
  mergeZone,
  splitRatioForZone,
  splitZone,
  type IdGen,
  type SplitDirection,
} from '../canvas/zone-ops'
import { PREVIEW_PANEL_ID } from '../preview/panel-ids'
import { PreviewPane } from '../preview/PreviewPane'
import { SplitPane } from '../preview/SplitPane'
import { usePanelCollapsed } from '../ui/CollapsibleSection'
import { useHistory } from '../state/useHistory'
import { useConfirm } from '../ui/ConfirmDialog'
import { useToast } from '../ui/Toaster'
import {
  clampBlockDimensionMm,
  ratioForChildSize,
  snapToMm,
} from '../block-configurator/dimension-ops'
import { mergeVariables } from '../block-configurator/variables-ops'
import { PALETTE_DRAG_TYPE, type PaletteDragPayload } from './BlockPalette'
import { bakeBlock, type PropValues } from './bake-block'
import {
  applyFloorClampToSplit,
  applyFloorClampToSplitWithClampInfo,
  computeMinLabelSizeWithConstraint,
  type Floor,
  type SplitClampInfo,
} from './floor-ops'
import { deepEqualBlockBody } from './promote-helpers'
import { BlockPalette } from './BlockPalette'
import { BlockPropsModal } from './BlockPropsModal'
import { TemplatePropertyPanel } from './TemplatePropertyPanel'
import {
  clearBakedInstanceAt,
  collectBakedInstances,
  propsToStrings,
  rebakeWithValues,
  rebakeWithVersion,
  type BakedInstanceEntry,
} from './block-instance-ops'
import { computeVersionStatus } from './staleness'
import type { VariableGroup } from './TemplateVariablesForm'
import { TemplateBlockInstanceTable } from './TemplateBlockInstanceTable'
import { VersionPickerPopover } from './VersionPickerPopover'

const PX_PER_MM_BASE = 4
// How long the inline drop-fit-rejection hint stays on the target zone —
// longer than the stacking toast's 5s AUTO_HIDE_MS so it outlives it and the
// user has time to read the exact numbers (issue #126).
const DROP_FIT_HINT_MS = 6000

// Module-stable filler: produces a plain empty Zone (no component) so that
// splitting in a template always yields "Drop a block here" cells — not
// blank text placeholders.
const emptyRegionFiller = (idGen: IdGen): Zone => ({ id: idGen() })

// #126: the rejection text is fed LAID-OUT zone mm (zone.w/zone.h), which come
// straight off unsnapped split ratios — raw interpolation printed "slot is only
// 55.00000000000001×80 mm", so the message is formatted at the same 1 dp as the
// Size (mm) columns. The COMPARISON has to tolerate that same residue or the
// two disagree: a 45mm block over the 44.99999999999999mm zone the table calls
// "45" would be refused by a toast reading "needs 45×30 mm, slot is only
// 45×40 mm". fitsInMm's 1e-6 covers residue only — see canvas/fit-mm.ts.
//
// Rounding alone was not enough, though. Nearest-tenth on BOTH numbers puts
// every sub-0.05mm shortfall back in the contradiction: a 94.7mm block over a
// 94.68mm zone is refused by "needs 94.7×30 mm, slot is only 94.7×40 mm". So
// this refusal — and only refusals — ceils what is needed and floors what is
// there, which makes the quoted numbers always account for the "no"
// (canvas/format-mm.ts carries the proof).
/** The route's `t`. computeFitError runs at module scope, so it takes one. */
type Translate = ReturnType<typeof useTranslation>['t']

function computeFitError(
  t: Translate,
  block: BlockDefinition,
  hostBox: { widthMm: number; heightMm: number }
): string | undefined {
  if (!fitsInMm(hostBox, block)) {
    return t('templateDesigner.toast.dropRejected', {
      name: block.name,
      blockWidth: formatMmCeil(block.widthMm),
      blockHeight: formatMmCeil(block.heightMm),
      hostWidth: formatMmFloor(hostBox.widthMm),
      hostHeight: formatMmFloor(hostBox.heightMm),
    })
  }
  return undefined
}

/**
 * Seed document for a brand-new template. `name` is SEEDED DATA (persisted on
 * the Template and shown in the galleries), so it is resolved ONCE from the
 * caller's `t()` in whatever language the author was working in and never
 * re-translated afterwards — same contract as `makeBlankBlock` (issue #154).
 */
function makeBlankTemplate(id: string, name: string): Template {
  return {
    id,
    name,
    widthMm: 100,
    heightMm: 80,
    rootZone: { id: 'root' },
  }
}

/** Unwrap a blockDefCache entry: only a real def passes; 'error'/missing → undefined. */
function defOf(entry: BlockDefinition | 'error' | undefined): BlockDefinition | undefined {
  return entry !== undefined && entry !== 'error' ? entry : undefined
}

export function TemplateDesignerRoute() {
  const adapter = useAdapter()
  const navigate = useNavigate()
  const { templateId } = useParams<{ templateId: string }>()
  const confirm = useConfirm()
  const { t } = useTranslation()
  // Stacking toast controller (Task 1). Used for the bulk-update summary and
  // the drop-failure/fit-rejection paths (issue #126) so multiple outcomes
  // can show BOTH toasts. The route's legacy single-slot `toast` state stays
  // for the other call sites (later cleanup) — see `toast`/`setToast` below.
  const toastStack = useToast()
  const isNewRoute = !templateId || templateId === 'new'

  const initialTemplate = useMemo(
    () =>
      makeBlankTemplate(
        templateId && templateId !== 'new' ? templateId : `draft-${Date.now()}`,
        t('templateDesigner.blank.name')
      ),
    // Recomputing on a locale switch is a no-op by design: `useHistory` and
    // `lastSavedRef` both read `initialTemplate` only on mount, so the seeded
    // name stays in the language the template was created in.
    [templateId, t]
  )

  const history = useHistory<Template>(initialTemplate)
  // Transient document during drag operations (outer-resize and divider-ratio
  // drags). Written on every pointermove; committed once to history on pointerup.
  // NOT visible to the dirty-check (lastSavedRef compares history.state).
  const [transientTemplate, setTransientTemplate] = useState<Template | undefined>(undefined)
  // All reads use the effective template so the canvas stays live during drag.
  const template = transientTemplate ?? history.state
  // Dimension lock (persists with the document): blocks the typed-input
  // commit path and the resize drag handles, not just the disabled styling.
  const dimensionsLocked = template.dimensionsLocked === true
  const [selectedPath, setSelectedPath] = useState<string[] | undefined>(undefined)
  const [zoom, setZoom] = useState(1)
  const [snapToGrid, setSnapToGrid] = useState(false)
  const [etag, setEtag] = useState<string | undefined>()
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [saveError, setSaveError] = useState<string | undefined>()
  const [conflictPending, setConflictPending] = useState(false)
  const [toast, setToast] = useState<string | undefined>()
  // Preview half's collapsed state (#189). It lives up here rather than in
  // PreviewPane because SplitPane — not the pane — renders the collapsed
  // strip, and the pane is unmounted while collapsed.
  const previewPanel = usePanelCollapsed(PREVIEW_PANEL_ID)
  // Persistent inline hint on the drop target when attemptDropOnZone rejects
  // a drop for fit (issue #126) — geometry is captured in mm so it re-scales
  // correctly if the user zooms while it's showing.
  const [dropFitHint, setDropFitHint] = useState<
    | {
        x: number
        y: number
        w: number
        h: number
        blockWidthMm: number
        blockHeightMm: number
        hostWidthMm: number
        hostHeightMm: number
      }
    | undefined
  >(undefined)
  // Load-state machine: 'loading' while the load effect is in-flight,
  // 'ok' when loaded (or for /new routes), 'not-found', or 'error'.
  const [loadState, setLoadState] = useState<'loading' | 'ok' | 'not-found' | 'error'>(
    isNewRoute ? 'ok' : 'loading'
  )
  const [loadError, setLoadError] = useState<string | undefined>()
  // Monotonic token: incremented on every load start (effect or Retry) and on
  // effect cleanup, so any in-flight load that no longer matches is stale and
  // must not set state (unmount or templateId change without a remount).
  const loadSeqRef = useRef(0)

  // Dirty tracking: compare present document identity against last-saved snapshot.
  // lastSavedRef is set on: initial blank (so a fresh untouched doc is never dirty),
  // after a successful save, and after a load resets the history.
  const lastSavedRef = useRef<Template>(initialTemplate)
  // Always compare history.state (not the transient doc) so a mid-drag state
  // doesn't falsely mark the doc dirty.
  const dirty = history.state !== lastSavedRef.current

  const [blockMetas, setBlockMetas] = useState<BlockDefinitionMeta[]>([])
  const [blocksLoading, setBlocksLoading] = useState(false)
  // Full block definitions for baked instances on the canvas, keyed
  // `blockId@version`. Needed because re-baking a value requires the pristine
  // `{var}` layout that `expansion` has already resolved away.
  // Sentinel values: `'error'` = load attempted but failed (retryable);
  // `null` is NOT used — the cache only holds BlockDefinition | 'error'.
  const [blockDefCache, setBlockDefCache] = useState<Record<string, BlockDefinition | 'error'>>({})
  const [activePick, setActivePick] = useState<
    { id: string; flavour: 'published' | 'draft' } | undefined
  >()
  const [pickedBlock, setPickedBlock] = useState<
    { block: BlockDefinition; flavour: 'published' | 'draft' } | undefined
  >()
  const [pendingDrop, setPendingDrop] = useState<
    | {
        block: BlockDefinition
        path: string[]
        flavour: 'published' | 'draft'
        existingBaked?: BakedBlockInstance
      }
    | undefined
  >(undefined)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerCtx, setPickerCtx] = useState<
    | {
        baked: BakedBlockInstance
        hostBox: { widthMm: number; heightMm: number }
        path: string[]
        versions: BlockVersionMeta[]
        draft: BlockDefinition | undefined
      }
    | undefined
  >(undefined)

  const effectivePxPerMm = PX_PER_MM_BASE * zoom

  const defaultCanvasProfile = useMemo(
    () => getPrinterProfile('zebra-zd220-203') ?? getPrinterProfiles()[0],
    []
  )

  const resolvedImages = useResolvedImages(template, defaultCanvasProfile)

  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(undefined), 4000)
    return () => clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    if (!dropFitHint) return
    const timer = setTimeout(() => setDropFitHint(undefined), DROP_FIT_HINT_MS)
    return () => clearTimeout(timer)
  }, [dropFitHint])

  // Full load sequence, shared by the mount/param-change effect and the
  // Retry button. `isCancelled` is checked after every await so a stale run
  // (superseded, unmounted) never sets state.
  const runLoad = useCallback(
    async (isCancelled: () => boolean) => {
      if (!adapter || !templateId || isNewRoute) return
      setLoadState('loading')
      setLoadError(undefined)
      const res = await adapter.resources.loadTemplate(templateId)
      if (isCancelled()) return
      if (res.status === 'ok') {
        history.reset(res.data.resource)
        setEtag(res.data.etag)
        // Loaded document is the clean baseline — not dirty.
        lastSavedRef.current = res.data.resource
        setLoadState('ok')
      } else if (res.error.code === 'NOT_FOUND') {
        setLoadState('not-found')
      } else {
        setLoadError(res.error.message)
        setLoadState('error')
      }
    },
    [adapter, templateId, isNewRoute, history]
  )

  // Start a load run under a fresh token. Any previously in-flight run
  // (effect- or Retry-started) is invalidated by the increment.
  const startLoad = useCallback(() => {
    const mySeq = ++loadSeqRef.current
    void runLoad(() => loadSeqRef.current !== mySeq)
  }, [runLoad])

  useEffect(() => {
    if (isNewRoute) {
      // Param can change without a remount — landing on /templates/new from a
      // failed id must reset the machine to the editable blank state.
      setLoadState('ok')
      setLoadError(undefined)
      return
    }
    if (!adapter) return
    startLoad()
    return () => {
      // Invalidate any in-flight load (effect- or Retry-started) on unmount
      // or adapter/templateId change.
      loadSeqRef.current++
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, templateId])

  // Re-fetches the block metadata list (`listBlocks`). Called on mount AND
  // after any mutation that changes a block's published version or draft
  // status (promote-on-drop, picker swap-to-draft promote, bulk update) — so
  // VersionChip / "NEWER AVAILABLE" / "Draft available" badges and "Update
  // all to latest" always compute from fresh data instead of the one-shot
  // snapshot taken when the route mounted.
  const refreshBlockMetas = useCallback(async () => {
    if (!adapter) return
    const res = await adapter.resources.listBlocks({ limit: 100 })
    if (res.status === 'ok') setBlockMetas(res.data.items)
  }, [adapter])

  useEffect(() => {
    if (!adapter) return
    let cancelled = false
    setBlocksLoading(true)
    void (async () => {
      await refreshBlockMetas()
      if (cancelled) return
      setBlocksLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [adapter, refreshBlockMetas])

  const bakedInstances = useMemo(
    () => collectBakedInstances(template.rootZone),
    [template.rootZone]
  )

  // Load full block defs for every distinct blockId@version on the canvas.
  useEffect(() => {
    if (!adapter) return
    const needed = Array.from(
      new Set(bakedInstances.map(i => `${i.baked.blockId}@${i.baked.blockVersion}`))
    )
    // Skip keys that are already loaded OR already marked as error (retryable via
    // onRetryBlockDef which removes the key before re-triggering this effect).
    const missing = needed.filter(k => !(k in blockDefCache))
    if (missing.length === 0) return
    let cancelled = false
    void (async () => {
      const loaded: Record<string, BlockDefinition | 'error'> = {}
      for (const key of missing) {
        const at = key.lastIndexOf('@')
        const blockId = key.slice(0, at)
        const version = Number(key.slice(at + 1))
        const res = await adapter.resources.loadBlock(blockId, version)
        // 'error' = load failed → group renders "Couldn't load…" + Retry button.
        loaded[key] = res?.status === 'ok' ? res.data.resource : 'error'
      }
      if (cancelled) return
      setBlockDefCache(prev => ({ ...prev, ...loaded }))
    })()
    return () => {
      cancelled = true
    }
  }, [adapter, bakedInstances, blockDefCache])

  const variablesLoading = useMemo(
    () =>
      bakedInstances.some(i => !(`${i.baked.blockId}@${i.baked.blockVersion}` in blockDefCache)),
    [bakedInstances, blockDefCache]
  )

  const variableGroups = useMemo<VariableGroup[]>(() => {
    const alignLabel = (p: BakedBlockPlacement): string =>
      `${p.vAlign.charAt(0).toUpperCase()}/${p.hAlign.charAt(0).toUpperCase()}`
    const displayKeyOf = (inst: (typeof bakedInstances)[number]): string => {
      const meta = blockMetas.find(m => m.id === inst.baked.blockId)
      const name = meta?.name ?? inst.baked.blockId
      return `${name} · v${inst.baked.blockVersion}`
    }
    const keyCount = new Map<string, number>()
    for (const inst of bakedInstances) {
      const k = displayKeyOf(inst)
      keyCount.set(k, (keyCount.get(k) ?? 0) + 1)
    }
    return bakedInstances.map(inst => {
      const meta = blockMetas.find(m => m.id === inst.baked.blockId)
      const name = meta?.name ?? inst.baked.blockId
      const cacheEntry = blockDefCache[`${inst.baked.blockId}@${inst.baked.blockVersion}`]
      const isDuplicate = (keyCount.get(displayKeyOf(inst)) ?? 0) > 1
      const loadFailed = cacheEntry === 'error'
      const def = defOf(cacheEntry)
      return {
        zoneId: inst.zoneId,
        path: inst.path,
        blockName: name,
        blockVersion: inst.baked.blockVersion,
        placementLabel: isDuplicate ? alignLabel(inst.baked.placement) : undefined,
        loadFailed,
        // Canonical variable set = layout `{var}` references overlaid with
        // persisted metadata (mirrors the block designer). Reading def.variables
        // alone misses bare references that pruneOrphans never persisted (e.g.
        // a `{lot}` with no edited type/default). undefined def -> undefined.
        variables: def ? mergeVariables(def.layout, def.variables ?? {}) : undefined,
        values: propsToStrings(inst.baked.props),
      }
    })
  }, [bakedInstances, blockMetas, blockDefCache])

  const onVariableCommit = useCallback(
    (group: VariableGroup, name: string, value: string) => {
      const entry = collectBakedInstances(history.state.rootZone).find(
        e => e.zoneId === group.zoneId
      )
      if (!entry) return
      const def = defOf(blockDefCache[`${entry.baked.blockId}@${entry.baked.blockVersion}`])
      if (!def) return
      const nextValues = { ...propsToStrings(entry.baked.props), [name]: value }
      const rebaked = rebakeWithValues(entry.baked, def, nextValues)
      history.update(prev => {
        // Read the host zone from `prev` (not history.state) so the node spread
        // and the replace share one snapshot — preserves the zone's borders/style.
        const node = findZoneByPath(prev.rootZone, entry.path)
        if (!node) return prev
        const replacement: Zone = { ...node, component: rebaked }
        return { ...prev, rootZone: replaceZoneAtPath(prev.rootZone, entry.path, replacement) }
      })
    },
    [history, blockDefCache]
  )

  /**
   * Retry loading the block def for a group identified by `zoneId`. Removes the
   * 'error' sentinel from the cache so the block-defs effect re-fetches it on
   * the next render cycle.
   */
  const onRetryBlockDef = useCallback(
    (zoneId: string) => {
      // Find the baked instance for this zone to get blockId@version key.
      const entry = collectBakedInstances(history.state.rootZone).find(e => e.zoneId === zoneId)
      if (!entry) return
      const key = `${entry.baked.blockId}@${entry.baked.blockVersion}`
      // Remove the error entry — the block-defs effect will re-fetch it.
      setBlockDefCache(prev => {
        if (prev[key] !== 'error') return prev
        const next = { ...prev }
        delete next[key]
        return next
      })
    },
    [history.state.rootZone]
  )

  const selectedZoneId = useMemo<string | undefined>(() => {
    if (!selectedPath) return undefined
    return zoneIdAtPath(template.rootZone, selectedPath)
  }, [template, selectedPath])

  const selectedZoneForPanel = useMemo<LaidOutZone | undefined>(() => {
    if (!selectedPath) return undefined
    const layout = computeZoneLayout(template.rootZone, {
      widthMm: template.widthMm,
      heightMm: template.heightMm,
    })
    return layout.find(
      z => z.path.length === selectedPath.length && z.path.every((p, i) => p === selectedPath[i])
    )
  }, [template, selectedPath])

  const selectedBorders = useMemo<RegionBorders | undefined>(() => {
    if (!selectedPath) return undefined
    let node: Zone = template.rootZone
    for (const seg of selectedPath) {
      const idx = seg === '0' ? 0 : seg === '1' ? 1 : undefined
      if (idx === undefined || !node.children) return undefined
      node = node.children[idx]
    }
    return node.borders
  }, [template, selectedPath])

  const onPickBlock = useCallback(
    async (id: string, flavour: 'published' | 'draft') => {
      if (!adapter) return
      const res =
        flavour === 'draft'
          ? await adapter.resources.loadBlock(id, 'draft')
          : await adapter.resources.loadBlock(id)
      if (res.status !== 'ok') return
      setActivePick({ id, flavour })
      setPickedBlock({ block: res.data.resource, flavour })
    },
    [adapter]
  )

  const onClearPick = useCallback(() => {
    setActivePick(undefined)
    setPickedBlock(undefined)
  }, [])

  // Promote (if draft) + bake + place a block at a path. Shared by the direct
  // drop path (default values, no modal) and the "Edit values" modal path
  // (re-bake of an existing instance, identity + placement preserved).
  const commitDrop = useCallback(
    async (
      ctx: {
        block: BlockDefinition
        flavour: 'published' | 'draft'
        path: string[]
        existingBaked?: BakedBlockInstance
      },
      values: PropValues
    ) => {
      if (!adapter) return
      const { block, flavour, path, existingBaked } = ctx
      let bakedBody: BlockDefinition = block

      if (flavour === 'draft') {
        const latestRes = await adapter.resources.loadBlock(block.id)
        const latestBody = latestRes.status === 'ok' ? latestRes.data.resource : undefined
        if (latestBody && deepEqualBlockBody(block, latestBody)) {
          bakedBody = latestBody
          setToast(t('templateDesigner.toast.noChanges', { version: latestBody.version }))
        } else {
          const promote = await adapter.resources.promoteBlockDraft(block.id, block)
          if (promote.status !== 'ok') {
            setSaveError(promote.error.message)
            return
          }
          bakedBody = { ...block, version: promote.data.version }
          setToast(
            t('templateDesigner.toast.publishedAndPlaced', {
              name: block.name,
              version: promote.data.version,
            })
          )
          // The draft was just consumed — refresh blockMetas so this block's
          // latestVersion/hasDraft is fresh (otherwise the newly-dropped
          // instance can show a stale "Draft available"/"NEWER AVAILABLE").
          void refreshBlockMetas()
        }
      }

      const idGen = existingBaked ? () => existingBaked.id : undefined
      const baked = bakeBlock(bakedBody, values, undefined, idGen)
      const finalBaked = existingBaked ? { ...baked, placement: existingBaked.placement } : baked
      history.update(prev => ({
        ...prev,
        rootZone: replaceZoneAtPath(prev.rootZone, path, {
          id: `bi-${Date.now()}`,
          component: finalBaked,
        }),
      }))
      setSelectedPath(undefined)
    },
    [adapter, history, refreshBlockMetas, t]
  )

  // Shared logic: attempt to drop `block` (with `flavour`) onto `zone`.
  // Called by both the click-to-pick path (onSelect) and the drag-and-drop path
  // (onZoneDrop). Handles fit-check → toast, occupied confirm, and commitDrop.
  // `clearPickState` is called before committing (or on fit rejection) so the
  // caller can decide whether to clear pick mode (click path) or not (drag path
  // has no pick state to clear).
  const attemptDropOnZone = useCallback(
    (
      block: BlockDefinition,
      flavour: 'published' | 'draft',
      zone: LaidOutZone,
      clearPickState: () => void
    ) => {
      const fitError = computeFitError(t, block, { widthMm: zone.w, heightMm: zone.h })
      if (fitError) {
        clearPickState()
        // Migrated off the legacy 4s single-slot toast onto the stacking
        // toastStack (issue #126), PLUS a persistent inline hint on the
        // target zone itself with the exact numbers.
        toastStack.error(fitError)
        setDropFitHint({
          x: zone.x,
          y: zone.y,
          w: zone.w,
          h: zone.h,
          blockWidthMm: block.widthMm,
          blockHeightMm: block.heightMm,
          hostWidthMm: zone.w,
          hostHeightMm: zone.h,
        })
        return
      }
      setDropFitHint(undefined)

      const existingBakedInZone =
        zone.component !== undefined &&
        (zone.component as { type?: string }).type === 'baked-block-instance'
          ? (zone.component as BakedBlockInstance)
          : undefined

      if (existingBakedInZone) {
        const existingName =
          blockMetas.find(m => m.id === existingBakedInZone.blockId)?.name ??
          existingBakedInZone.blockId
        void (async () => {
          const ok = await confirm({
            title: t('confirm.replaceInstance.title', { name: existingName }),
            body: t('confirm.replaceInstance.body'),
            confirmLabel: t('common.replace'),
            danger: true,
          })
          if (!ok) {
            // Cancel: stay in pick mode (click path) — don't clear pick state.
            return
          }
          clearPickState()
          void commitDrop({ block, flavour, path: zone.path }, {})
        })()
        return
      }

      // Empty zone: commit immediately.
      clearPickState()
      void commitDrop({ block, flavour, path: zone.path }, {})
    },
    [commitDrop, blockMetas, confirm, toastStack, t]
  )

  const onSelect = useCallback(
    (zone: LaidOutZone) => {
      if (pickedBlock) {
        attemptDropOnZone(pickedBlock.block, pickedBlock.flavour, zone, () => {
          setActivePick(undefined)
          setPickedBlock(undefined)
        })
        return
      }
      setSelectedPath(zone.path)
    },
    [pickedBlock, attemptDropOnZone]
  )

  // Drag-and-drop: resolve the payload, load the block definition (same as
  // onPickBlock), then converge on attemptDropOnZone (same as click-to-pick).
  const onZoneDrop = useCallback(
    (payload: string, zone: LaidOutZone) => {
      if (!adapter) return
      let parsed: PaletteDragPayload
      try {
        parsed = JSON.parse(payload) as PaletteDragPayload
      } catch {
        return
      }
      const { blockId, flavour } = parsed
      void (async () => {
        const res =
          flavour === 'draft'
            ? await adapter.resources.loadBlock(blockId, 'draft')
            : await adapter.resources.loadBlock(blockId)
        if (res.status !== 'ok') {
          // Previously a bare return — the failure vanished with no feedback.
          toastStack.error(t('templateDesigner.toast.blockLoadFailed'))
          return
        }
        const block = res.data.resource
        // Drag path has no pick state — noop clear.
        attemptDropOnZone(block, flavour, zone, () => {})
      })()
    },
    [adapter, attemptDropOnZone, toastStack, t]
  )

  const onSplit = useCallback(
    (zone: LaidOutZone, direction: SplitDirection) => {
      const ratio = splitRatioForZone(zone, direction)
      history.update(prev => ({
        ...prev,
        rootZone: splitZone(
          prev.rootZone,
          zone.path,
          direction,
          ratio,
          undefined,
          emptyRegionFiller
        ),
      }))
      setSelectedPath(undefined)
    },
    [history]
  )

  const onMergeAtPath = useCallback(
    async (path: string[]) => {
      // Walk the current root following the index-based path to the split node.
      let node: Zone = template.rootZone
      for (const seg of path) {
        const idx = Number(seg)
        if (!node.children || Number.isNaN(idx) || (idx !== 0 && idx !== 1)) return
        node = node.children[idx]
      }
      if (!node.children || node.children.length !== 2 || !node.split) return
      const leftHas = hasContent(node.children[0])
      const rightHas = hasContent(node.children[1])
      const bothHaveContent = leftHas && rightHas

      // Instant merge ONLY when both sides are empty. Any content on EITHER
      // side routes through the confirm dialog — even the "no data loss"
      // one-side case, since deleting the divider still restructures the
      // layout (the surviving content moves into the merged region).
      if (leftHas || rightHas) {
        const ok = await confirm({
          title: t('confirm.deleteDivider.title'),
          body: bothHaveContent
            ? t('confirm.deleteDivider.bodyBothContent')
            : t('confirm.deleteDivider.bodyMerge'),
          confirmLabel: t('common.delete'),
          danger: bothHaveContent,
        })
        if (!ok) return
        setSelectedPath(undefined)
        const keep: 0 | 1 | 'none' = bothHaveContent ? 'none' : leftHas ? 0 : 1
        history.update(prev => ({
          ...prev,
          rootZone: mergeZone(prev.rootZone, path, keep, undefined, emptyRegionFiller),
        }))
        return
      }

      // Both empty — silent instant merge (clear selection: a selected
      // descendant may vanish).
      setSelectedPath(undefined)
      history.update(prev => ({
        ...prev,
        rootZone: mergeZone(prev.rootZone, path, 0, undefined, emptyRegionFiller),
      }))
    },
    [template.rootZone, history, confirm, t]
  )

  // Block-aware floor for the outer label resize: shrinking past this would
  // overflow a placed block out of its cell. Stable across a resize drag —
  // outer resize never changes split ratios, so it depends only on rootZone.
  const minLabel = useMemo(
    () => computeMinLabelSizeWithConstraint(template.rootZone),
    [template.rootZone]
  )

  // Resolve a blockId to its display name (falls back to instanceId).
  const resolveBlockName = useCallback(
    (blockId: string, instanceId: string): string =>
      blockMetas.find(m => m.id === blockId)?.name ?? instanceId,
    [blockMetas]
  )

  // Tracks whether the current outer-resize drag gesture hit the block floor. Set per
  // pointermove when clamped; read and cleared once on pointerup (commitDragDimensions).
  const outerDragClampedRef = useRef<Floor | undefined>(undefined)
  // Tracks whether the current divider drag gesture hit a block floor. Set per
  // onSplitRatioChange when clamped; read and cleared once on drag end.
  const splitDragClampedRef = useRef<SplitClampInfo | undefined>(undefined)

  const onSplitRatioChange = useCallback(
    (path: string[], rawRatio: number) => {
      // Write to transient state during the drag so we don't flood history.
      // Re-base off `transientTemplate ?? history.state` (unlike
      // updateDimensionsFromPointer, which re-bases off `history.state`):
      // ratio updates must compose across the in-progress transient so a drag
      // touching multiple dividers keeps earlier edits, whereas dimension
      // writes are absolute pointer-derived values that fully replace the
      // dragged fields each move.
      const base = transientTemplate ?? history.state
      const info = applyFloorClampToSplitWithClampInfo(
        base.rootZone,
        { widthMm: base.widthMm, heightMm: base.heightMm },
        path,
        rawRatio
      )
      // Record the first clamp event during this gesture (ref persists across moves).
      if (info.clampedSide && info.clampingFloor && !splitDragClampedRef.current) {
        splitDragClampedRef.current = info
      }
      setTransientTemplate({ ...base, rootZone: info.zone })
    },
    [history.state, transientTemplate]
  )

  const onSplitRatioDragEnd = useCallback(() => {
    if (transientTemplate !== undefined) {
      history.set(transientTemplate)
      setTransientTemplate(undefined)
    }
    // Fire ONE info toast if the divider drag was clamped by the block floor.
    const info = splitDragClampedRef.current
    if (info?.clampingFloor) {
      splitDragClampedRef.current = undefined
      const floor = info.clampingFloor
      const name = resolveBlockName(floor.blockId, floor.instanceId)
      toastStack.info(t('templateDesigner.toast.minRegion', { name }))
    } else {
      splitDragClampedRef.current = undefined
    }
  }, [transientTemplate, history, resolveBlockName, toastStack, t])

  const onPlacementChange = useCallback(
    ({
      hAlign,
      vAlign,
    }: {
      hAlign: BakedBlockPlacement['hAlign']
      vAlign: BakedBlockPlacement['vAlign']
    }) => {
      if (!selectedPath) return
      history.update(prev => {
        const layout = computeZoneLayout(prev.rootZone, {
          widthMm: prev.widthMm,
          heightMm: prev.heightMm,
        })
        const selected = layout.find(
          z =>
            z.path.length === selectedPath.length && z.path.every((p, i) => p === selectedPath[i])
        )
        if (!selected || (selected.component as unknown) === undefined) return prev
        if (selected.component.type !== 'baked-block-instance') return prev
        const baked = selected.component
        const nextBaked: BakedBlockInstance = {
          ...baked,
          placement: { ...baked.placement, hAlign, vAlign },
        }
        const originalZone = findZoneByPath(prev.rootZone, selectedPath)
        if (!originalZone) return prev
        const updatedZone = { ...originalZone, component: nextBaked }
        return { ...prev, rootZone: replaceZoneAtPath(prev.rootZone, selectedPath, updatedZone) }
      })
    },
    [selectedPath, history]
  )

  const onSetEdge = useCallback(
    (edge: keyof RegionBorders, value: BorderEdge | undefined) => {
      if (!selectedPath) return
      history.update(prev => {
        const dims = { widthMm: prev.widthMm, heightMm: prev.heightMm }
        const partner = findCoincidentEdge(prev.rootZone, dims, selectedPath, edge)
        let next = setZoneBordersAtPath(prev.rootZone, selectedPath, edge, value)
        if (partner) next = setZoneBordersAtPath(next, partner.path, partner.edge, value)
        return { ...prev, rootZone: next }
      })
    },
    [history, selectedPath]
  )

  const onZoneDimensionsChange = useCallback(
    ({ widthMm, heightMm }: { widthMm: number; heightMm: number }) => {
      if (!selectedPath) return
      // Root zone — mutate template dimensions directly (no-op when the
      // dimension lock is on; the label size must not change by accident).
      if (selectedPath.length === 0) {
        if (dimensionsLocked) return
        history.update(prev => ({
          ...prev,
          widthMm: clampBlockDimensionMm(widthMm),
          heightMm: clampBlockDimensionMm(heightMm),
        }))
        return
      }
      // Child zone — find parent split, derive raw ratio, then apply floor clamp.
      history.update(prev => {
        const parentPath = selectedPath.slice(0, -1)
        const childIndex = selectedPath[selectedPath.length - 1]
        const parent = findZoneByPath(prev.rootZone, parentPath)
        if (!parent || !parent.split || !parent.children) return prev
        const axis = parent.split.direction
        const parentBox = resolveBoxAtPath(prev.rootZone, parentPath, {
          x: 0,
          y: 0,
          w: prev.widthMm,
          h: prev.heightMm,
        })
        if (!parentBox) return prev
        const parentTotal = axis === 'row' ? parentBox.w : parentBox.h
        const desiredChildMm = axis === 'row' ? widthMm : heightMm
        // For child[1]: convert its desired size to the equivalent child[0] size.
        const childAMm = childIndex === '0' ? desiredChildMm : parentTotal - desiredChildMm
        const rawRatio = ratioForChildSize(childAMm, parentTotal)
        return {
          ...prev,
          rootZone: applyFloorClampToSplit(
            prev.rootZone,
            { widthMm: prev.widthMm, heightMm: prev.heightMm },
            parentPath,
            rawRatio
          ),
        }
      })
    },
    [selectedPath, history, dimensionsLocked]
  )

  const onRename = useCallback(
    (name: string) => {
      history.update(prev => ({ ...prev, name }))
    },
    [history]
  )

  const onToggleDimensionsLock = useCallback(() => {
    history.update(prev => ({ ...prev, dimensionsLocked: !(prev.dimensionsLocked === true) }))
  }, [history])

  const onChangePrintRotation = useCallback(
    (next: PrintRotation) => {
      history.update(prev => ({ ...prev, printRotation: next }))
    },
    [history]
  )

  const onChangeWidth = useCallback(
    (widthMm: number) => {
      if (dimensionsLocked) return
      const clamped = clampBlockDimensionMm(widthMm, minLabel.widthMm)
      if (clamped > widthMm && minLabel.widthConstraint) {
        const c = minLabel.widthConstraint
        const name = resolveBlockName(c.blockId, c.instanceId)
        // formatMm keeps this quoting mm the way the rest of the UI does; the
        // floor itself is already whole (computeMinLabelSizeWithConstraint
        // ceils it), so unlike the drop-fit hint there is no residue to scrub.
        toastStack.info(
          t('templateDesigner.toast.minLabel', {
            width: formatMm(minLabel.widthMm),
            height: formatMm(minLabel.heightMm),
            name,
          })
        )
      }
      history.update(prev => ({ ...prev, widthMm: clamped }))
    },
    [history, minLabel, resolveBlockName, toastStack, dimensionsLocked, t]
  )

  const onChangeHeight = useCallback(
    (heightMm: number) => {
      if (dimensionsLocked) return
      const clamped = clampBlockDimensionMm(heightMm, minLabel.heightMm)
      if (clamped > heightMm && minLabel.heightConstraint) {
        const c = minLabel.heightConstraint
        const name = resolveBlockName(c.blockId, c.instanceId)
        // formatMm keeps this quoting mm the way the rest of the UI does; the
        // floor itself is already whole (computeMinLabelSizeWithConstraint
        // ceils it), so unlike the drop-fit hint there is no residue to scrub.
        toastStack.info(
          t('templateDesigner.toast.minLabel', {
            width: formatMm(minLabel.widthMm),
            height: formatMm(minLabel.heightMm),
            name,
          })
        )
      }
      history.update(prev => ({ ...prev, heightMm: clamped }))
    },
    [history, minLabel, resolveBlockName, toastStack, dimensionsLocked, t]
  )

  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const canvasPaneRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<'right' | 'bottom' | 'corner' | undefined>(undefined)

  const updateDimensionsFromPointer = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dimensionsLocked) return
      if (!dragging) return
      const container = canvasContainerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      // Snap first (1mm fine / 5mm coarse, same as the block route), THEN the
      // block-floor clamp — so the floor always wins over a snapped-down value.
      const snapMm = snapToGrid ? 5 : 1
      const rawWidthMm = snapToMm((e.clientX - rect.left) / effectivePxPerMm, snapMm)
      const rawHeightMm = snapToMm((e.clientY - rect.top) / effectivePxPerMm, snapMm)
      const widthFromPointer = clampBlockDimensionMm(rawWidthMm, minLabel.widthMm)
      const heightFromPointer = clampBlockDimensionMm(rawHeightMm, minLabel.heightMm)
      // Track whether the block floor is actively clamping this gesture.
      if (dragging === 'right' || dragging === 'corner') {
        if (widthFromPointer > rawWidthMm && minLabel.widthConstraint) {
          outerDragClampedRef.current = minLabel.widthConstraint
        }
      }
      if (dragging === 'bottom' || dragging === 'corner') {
        if (heightFromPointer > rawHeightMm && minLabel.heightConstraint) {
          // Prefer the width constraint if already set (for 'corner' both axes can clamp —
          // the toast message covers W × H so either constraint is sufficient).
          outerDragClampedRef.current = outerDragClampedRef.current ?? minLabel.heightConstraint
        }
      }
      // Write to transient state — NOT history — so every pointermove doesn't
      // flood the 50-entry undo stack. One history entry is committed on pointerup.
      const base = history.state
      if (dragging === 'right') {
        setTransientTemplate({ ...base, widthMm: widthFromPointer })
      } else if (dragging === 'bottom') {
        setTransientTemplate({ ...base, heightMm: heightFromPointer })
      } else {
        setTransientTemplate({ ...base, widthMm: widthFromPointer, heightMm: heightFromPointer })
      }
    },
    [dragging, effectivePxPerMm, history.state, minLabel, snapToGrid, dimensionsLocked]
  )

  const commitDragDimensions = useCallback(() => {
    if (transientTemplate !== undefined) {
      history.set(transientTemplate)
      setTransientTemplate(undefined)
    }
    setDragging(undefined)
    // Fire ONE info toast if the drag was clamped by the block floor.
    const clamped = outerDragClampedRef.current
    if (clamped) {
      outerDragClampedRef.current = undefined
      const name = resolveBlockName(clamped.blockId, clamped.instanceId)
      toastStack.info(
        t('templateDesigner.toast.minLabel', {
          width: formatMm(minLabel.widthMm),
          height: formatMm(minLabel.heightMm),
          name,
        })
      )
    }
  }, [transientTemplate, history, resolveBlockName, toastStack, minLabel, t])

  // The "Edit values" modal still routes through here (drop now bypasses it).
  const confirmBlockDrop = useCallback(
    async (values: PropValues) => {
      if (!pendingDrop) return
      await commitDrop(pendingDrop, values)
      setPendingDrop(undefined)
    },
    [pendingDrop, commitDrop]
  )

  const openPopoverForBaked = useCallback(
    async (
      baked: BakedBlockInstance,
      path: string[],
      hostBox: { widthMm: number; heightMm: number }
    ) => {
      if (!adapter) return
      const [vRes, dRes] = await Promise.all([
        adapter.resources.listBlockVersions(baked.blockId),
        adapter.resources.loadBlock(baked.blockId, 'draft'),
      ])
      const versions = vRes.status === 'ok' ? vRes.data : []
      const draft = dRes.status === 'ok' ? dRes.data.resource : undefined
      // Lazily load defs for listed versions not already cached so the popover
      // can show the variable-set diff for the versions you'd switch TO
      // (blockDefCache otherwise only holds versions currently baked).
      // Individual failures are cached as 'error' — those rows just show no diff.
      const missing = versions.filter(v => !(`${baked.blockId}@${v.version}` in blockDefCache))
      if (missing.length > 0) {
        const loaded = await Promise.all(
          missing.map(async v => {
            const res = await adapter.resources.loadBlock(baked.blockId, v.version)
            return [
              `${baked.blockId}@${v.version}`,
              res?.status === 'ok' ? res.data.resource : ('error' as const),
            ] as const
          })
        )
        setBlockDefCache(prev => {
          const next = { ...prev }
          // Don't overwrite entries the baked-instances effect may have landed
          // concurrently — first write wins, both load the same content.
          for (const [k, def] of loaded) if (!(k in next)) next[k] = def
          return next
        })
      }
      setPickerCtx({ baked, hostBox, path, versions, draft })
      setPickerOpen(true)
    },
    [adapter, blockDefCache]
  )

  const onPickerSwap = useCallback(
    async (version: number) => {
      if (!adapter || !pickerCtx) return
      const res = await adapter.resources.loadBlock(pickerCtx.baked.blockId, version)
      if (res.status !== 'ok') return
      const rebaked = rebakeWithVersion(pickerCtx.baked, res.data.resource, pickerCtx.hostBox)
      history.update(prev => ({
        ...prev,
        rootZone: replaceZoneAtPath(prev.rootZone, pickerCtx.path, {
          id: `bi-${Date.now()}`,
          component: rebaked,
        }),
      }))
      setPickerOpen(false)
      setPickerCtx(undefined)
    },
    [adapter, history, pickerCtx]
  )

  const onPickerSwapDraft = useCallback(async () => {
    if (!adapter || !pickerCtx || !pickerCtx.draft) return
    const draftBody = pickerCtx.draft

    // Same-content skip: load the latest published body and compare.
    const latestVersionMeta = [...pickerCtx.versions].sort((a, b) => b.version - a.version)[0]
    const latestRes = latestVersionMeta
      ? await adapter.resources.loadBlock(pickerCtx.baked.blockId, latestVersionMeta.version)
      : undefined
    const latestBody = latestRes?.status === 'ok' ? latestRes.data.resource : undefined

    let pickedBody: BlockDefinition

    if (latestBody && deepEqualBlockBody(draftBody, latestBody)) {
      pickedBody = latestBody
      setToast(t('templateDesigner.toast.noChanges', { version: latestBody.version }))
    } else {
      const promote = await adapter.resources.promoteBlockDraft(pickerCtx.baked.blockId, draftBody)
      if (promote.status !== 'ok') {
        setSaveError(promote.error.message)
        return
      }
      pickedBody = { ...draftBody, version: promote.data.version }
      setToast(
        t('templateDesigner.toast.publishedAndSwapped', {
          name: draftBody.name,
          version: promote.data.version,
        })
      )
      // The draft was just consumed — refresh blockMetas so this block's
      // latestVersion/hasDraft is fresh for every instance/badge referencing it.
      void refreshBlockMetas()
    }

    const rebaked = rebakeWithVersion(pickerCtx.baked, pickedBody, pickerCtx.hostBox)
    history.update(prev => ({
      ...prev,
      rootZone: replaceZoneAtPath(prev.rootZone, pickerCtx.path, {
        id: `bi-${Date.now()}`,
        component: rebaked,
      }),
    }))
    setPickerOpen(false)
    setPickerCtx(undefined)
  }, [adapter, pickerCtx, history, refreshBlockMetas, t])

  const onPickerDetach = useCallback(() => {
    if (!pickerCtx) return
    history.update(prev => ({
      ...prev,
      rootZone: clearBakedInstanceAt(prev.rootZone, pickerCtx.path),
    }))
    setPickerOpen(false)
    setPickerCtx(undefined)
  }, [history, pickerCtx])

  // Compute which instances are "newer-available" (have a published update ready).
  // Draft-available instances are intentionally excluded from bulk update — bulk
  // means "to latest PUBLISHED"; drafts require explicit promotion via the picker.
  const staleInstances = useMemo<BakedInstanceEntry[]>(
    () =>
      bakedInstances.filter(entry => {
        const meta = blockMetas.find(m => m.id === entry.baked.blockId)
        const status = computeVersionStatus(
          entry.baked.blockVersion,
          meta?.latestVersion,
          meta?.hasDraft ?? false
        )
        // Only 'newer-available' qualifies — 'draft-available' is intentionally
        // skipped because bulk update targets published versions only.
        return status === 'newer-available'
      }),
    [bakedInstances, blockMetas]
  )

  const onBulkUpdateToLatest = useCallback(async () => {
    if (!adapter || staleInstances.length === 0) return
    const succeeded: string[] = []
    const failed: string[] = []

    // Snapshot the current zone tree once — apply all swaps onto it serially.
    let nextRoot = history.state.rootZone

    for (const entry of staleInstances) {
      const meta = blockMetas.find(m => m.id === entry.baked.blockId)
      if (!meta?.latestVersion) continue

      const res = await adapter.resources.loadBlock(entry.baked.blockId, meta.latestVersion)
      if (res.status !== 'ok') {
        failed.push(meta.name ?? entry.baked.blockId)
        continue
      }
      const latestDef = res.data.resource

      // Fit-check: compute the host zone dims from the current layout.
      const layout = computeZoneLayout(nextRoot, {
        widthMm: history.state.widthMm,
        heightMm: history.state.heightMm,
      })
      const hostZone = layout.find(z => z.id === entry.zoneId)
      if (!hostZone) {
        failed.push(meta.name ?? entry.baked.blockId)
        continue
      }
      const hostBox = { widthMm: hostZone.w, heightMm: hostZone.h }
      const fitErr = computeFitError(t, latestDef, hostBox)
      if (fitErr) {
        failed.push(meta.name ?? entry.baked.blockId)
        continue
      }

      const rebaked = rebakeWithVersion(entry.baked, latestDef, hostBox)
      nextRoot = replaceZoneAtPath(nextRoot, entry.path, {
        id: `bi-${Date.now()}`,
        component: rebaked,
      })
      succeeded.push(meta.name ?? entry.baked.blockId)
    }

    if (succeeded.length > 0) {
      // Capture nextRoot in closure; `prev` is ignored because all replacements
      // were built from the snapshot above (serial loop, no concurrent edits).
      const finalRoot = nextRoot
      history.update(prev => ({ ...prev, rootZone: finalRoot }))
      toastStack.success(
        t('templateDesigner.toast.bulkUpdated', { count: succeeded.length })
      )
      // Re-fetch blockMetas so every badge (this run's instances AND any
      // other instance sharing a just-updated block) computes from fresh
      // data, not the snapshot taken when the route mounted.
      void refreshBlockMetas()
    }
    if (failed.length > 0) {
      toastStack.error(
        t('templateDesigner.toast.bulkUpdateFailed', {
          count: failed.length,
          names: failed.join(', '),
        })
      )
    }
  }, [adapter, staleInstances, blockMetas, history, toastStack, refreshBlockMetas, t])

  const onPanelEditValues = useCallback(
    async (baked: BakedBlockInstance, path: string[]) => {
      if (!adapter) return
      const res = await adapter.resources.loadBlock(baked.blockId, baked.blockVersion)
      if (res.status !== 'ok') {
        setSaveError(res.error.message)
        return
      }
      setPendingDrop({
        block: res.data.resource,
        path,
        flavour: 'published',
        existingBaked: baked,
      })
    },
    [adapter]
  )

  const onPanelDetach = useCallback(
    (_baked: BakedBlockInstance, path: string[]) => {
      history.update(prev => ({
        ...prev,
        rootZone: clearBakedInstanceAt(prev.rootZone, path),
      }))
      setSelectedPath(undefined)
    },
    [history]
  )

  const onSave = useCallback(async () => {
    if (!adapter) return
    setSaveStatus('saving')
    setSaveError(undefined)
    const res = await adapter.resources.saveTemplate(template, etag ? { ifMatch: etag } : undefined)
    if (res.status === 'ok') {
      setEtag(res.data.etag)
      setSaveStatus('saved')
      setConflictPending(false)
      // Successful save: update the clean-state baseline so the dirty indicator clears.
      lastSavedRef.current = template
      if (isNewRoute) {
        navigate(`/templates/${encodeURIComponent(template.id)}`, { replace: true })
      }
      setTimeout(() => setSaveStatus('idle'), 2000)
    } else if (res.error.code === 'CONFLICT') {
      setSaveStatus('error')
      setSaveError(undefined)
      setConflictPending(true)
    } else {
      setSaveStatus('error')
      setSaveError(res.error.message)
    }
  }, [adapter, template, etag, isNewRoute, navigate])

  const onConflictReload = useCallback(async () => {
    // Reload discards the user's local edits (history.reset wipes the doc +
    // undo stack) — guard it like every other destructive action.
    const ok = await confirm({
      title: t('confirm.discardChanges.title'),
      body: t('confirm.discardChanges.body'),
      confirmLabel: t('common.reload'),
      danger: true,
    })
    if (!ok) return // keep the banner + local edits
    setConflictPending(false)
    setSaveStatus('idle')
    startLoad()
  }, [confirm, startLoad, t])

  const onConflictOverwrite = useCallback(async () => {
    if (!adapter || !templateId) return
    setSaveStatus('saving')
    setSaveError(undefined)
    // Silently refetch the template to get the CURRENT etag, then re-save with it.
    const probe = await adapter.resources.loadTemplate(templateId)
    if (probe.status !== 'ok') {
      // Can't verify the latest version — NEVER fall through to an
      // unconditional (no-ifMatch) save. Abort; the banner stays armed.
      setSaveStatus('error')
      setSaveError(t('saveError.verifyFailed'))
      return
    }
    const res = await adapter.resources.saveTemplate(template, { ifMatch: probe.data.etag })
    if (res.status === 'ok') {
      setConflictPending(false)
      setEtag(res.data.etag)
      setSaveStatus('saved')
      lastSavedRef.current = template
      setTimeout(() => setSaveStatus('idle'), 2000)
    } else if (res.error.code === 'CONFLICT') {
      // Etag moved again between probe and re-save — keep the banner armed,
      // never leak the raw adapter message.
      setSaveStatus('error')
      setConflictPending(true)
    } else {
      setSaveStatus('error')
      setSaveError(res.error.message)
      setConflictPending(false)
    }
  }, [adapter, templateId, template, t])

  const onWheelZoom = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (!(e.ctrlKey || e.metaKey)) return
    e.preventDefault()
    setZoom(prev => clampZoom(prev - e.deltaY * 0.001))
  }, [])

  const { onPaneClick } = useCanvasDeselect({
    // Click outside the canvas label while in pick mode cancels pick (same as
    // Escape). The banner disappears and zones revert to normal styling.
    onDeselect: () => {
      if (pickedBlock) {
        onClearPick()
      } else {
        setSelectedPath(undefined)
      }
    },
    onEscape: () => {
      if (pickedBlock) onClearPick()
      else setSelectedPath(undefined)
    },
    escapeEnabled: !pendingDrop && !pickerOpen,
  })

  // Unsaved-changes guard: beforeunload + LeftNav confirm while dirty.
  // Only active in the 'ok' state — non-ok panes can't be saved and must
  // never prompt the user when they navigate away.
  useUnsavedGuard(loadState === 'ok' && dirty)

  if (loadState === 'loading') {
    return (
      <div
        data-testid="route-loading"
        className="flex h-full items-center justify-center text-sm text-stone-500 dark:text-stone-400"
      >
        {t('routeState.loading')}
      </div>
    )
  }

  if (loadState === 'not-found') {
    return (
      <div
        data-testid="route-not-found"
        className="flex h-full flex-col items-center justify-center gap-4 text-center"
      >
        <p className="text-base font-medium text-stone-700 dark:text-stone-300">
          {t('routeState.templateNotFound')}
        </p>
        <Link
          to="/templates"
          className="rounded bg-sky-600 px-4 py-2 text-sm text-white hover:bg-sky-700"
        >
          {t('routeState.backToGallery')}
        </Link>
      </div>
    )
  }

  if (loadState === 'error') {
    return (
      <div
        data-testid="route-error"
        className="flex h-full flex-col items-center justify-center gap-4 text-center"
      >
        <p className="text-base font-medium text-stone-700 dark:text-stone-300">
          {t('routeState.templateLoadFailed')}
        </p>
        {loadError && <p className="text-sm text-stone-500 dark:text-stone-400">{loadError}</p>}
        <button
          type="button"
          data-testid="route-error-retry"
          onClick={startLoad}
          className="rounded bg-sky-600 px-4 py-2 text-sm text-white hover:bg-sky-700"
        >
          {t('routeState.retry')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-stone-200 bg-white px-6 py-3 dark:border-stone-800 dark:bg-stone-900">
        <div className="flex flex-col">
          <input
            value={template.name}
            onChange={e => onRename(e.target.value)}
            data-testid="td-name"
            className="bg-transparent text-lg font-semibold outline-none focus:rounded focus:bg-stone-100 focus:px-2 dark:focus:bg-stone-800"
          />
          <p className="text-xs text-stone-500 dark:text-stone-400">
            {t('templateDesigner.dimensions', {
              width: template.widthMm,
              height: template.heightMm,
            })}{' '}
            · id: <code>{template.id}</code>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CanvasControls
            zoom={zoom}
            onZoomChange={setZoom}
            onZoomToFit={() => {
              const pane = canvasPaneRef.current
              if (!pane) {
                setZoom(1)
                return
              }
              setZoom(
                fitZoomToViewport(
                  { widthMm: template.widthMm, heightMm: template.heightMm },
                  { widthPx: pane.clientWidth, heightPx: pane.clientHeight },
                  PX_PER_MM_BASE
                )
              )
            }}
            snapToGrid={snapToGrid}
            onToggleSnap={() => setSnapToGrid(v => !v)}
          />
          <button
            type="button"
            onClick={history.undo}
            disabled={!history.canUndo}
            className="rounded border border-stone-300 px-3 py-1 text-xs text-stone-700 disabled:opacity-40 hover:enabled:bg-stone-100 dark:border-stone-700 dark:text-stone-300 dark:hover:enabled:bg-stone-800"
          >
            {t('templateDesigner.undo')}
          </button>
          <button
            type="button"
            onClick={history.redo}
            disabled={!history.canRedo}
            className="rounded border border-stone-300 px-3 py-1 text-xs text-stone-700 disabled:opacity-40 hover:enabled:bg-stone-100 dark:border-stone-700 dark:text-stone-300 dark:hover:enabled:bg-stone-800"
          >
            {t('templateDesigner.redo')}
          </button>
          <button
            type="button"
            data-testid="td-save"
            onClick={onSave}
            disabled={!adapter || saveStatus === 'saving'}
            className="rounded bg-sky-600 px-3 py-1 text-xs text-white disabled:opacity-40 hover:enabled:bg-sky-700"
          >
            {saveStatus === 'saving'
              ? t('templateDesigner.saving')
              : saveStatus === 'saved'
                ? t('templateDesigner.saved')
                : dirty
                  ? t('templateDesigner.saveDirty')
                  : t('templateDesigner.save')}
          </button>
        </div>
      </div>
      {conflictPending && (
        <div
          data-testid="conflict-banner"
          className="flex items-center gap-3 bg-amber-50 px-6 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          <span>{t('templateDesigner.conflict.message')}</span>
          <button
            type="button"
            data-testid="conflict-reload"
            onClick={onConflictReload}
            className="rounded border border-amber-400 px-2 py-0.5 hover:bg-amber-100 dark:border-amber-600 dark:hover:bg-amber-900"
          >
            {t('templateDesigner.conflict.reload')}
          </button>
          <button
            type="button"
            data-testid="conflict-overwrite"
            onClick={onConflictOverwrite}
            className="rounded border border-rose-400 px-2 py-0.5 text-rose-700 hover:bg-rose-50 dark:border-rose-600 dark:text-rose-300 dark:hover:bg-rose-950"
          >
            {t('templateDesigner.conflict.overwrite')}
          </button>
        </div>
      )}
      {saveError && (
        <div className="bg-rose-50 px-6 py-2 text-xs text-rose-700 dark:bg-rose-950 dark:text-rose-300">
          {saveError}
        </div>
      )}
      <SplitPane
        initialTopPct={60}
        storageKey="ld:designer-split"
        collapsedBottom={previewPanel.collapsed}
        onExpandBottom={previewPanel.toggle}
        bottomLabel={t('preview.title')}
        bottomPanelId={PREVIEW_PANEL_ID}
        // Only one of the two toggles carrying this ref is ever mounted (the
        // strip's while collapsed, the toolbar's while expanded), so it is
        // exactly the handover focus needs.
        bottomToggleRef={previewPanel.toggleRef}
      >
        {/* `h-full`, not `flex-1` (#190): SplitPane's half wrapper is a
            display:block flex ITEM, so `flex-1` here is dead CSS and this row
            would size to its content. That left the palette, the canvas column
            and the property panel all unbounded — their `overflow-auto` never
            engaged and SplitPane's `overflow-hidden` clipped the overflow with
            no scrollbar anywhere. `min-h-0` is not a fix: every box in this
            chain already has its automatic minimum size zeroed by its overflow
            value; what was missing is a DEFINITE height. Mirrors
            BlockConfiguratorRoute.tsx's equivalent row, which was always
            `h-full` and therefore never showed the defect. */}
        <div data-testid="td-designer-row" className="flex h-full overflow-hidden">
          <BlockPalette
            items={blockMetas}
            loading={blocksLoading}
            activePick={activePick}
            onPick={onPickBlock}
            onClearPick={onClearPick}
          />
          <div
            className="flex flex-1 flex-col overflow-auto bg-stone-100 dark:bg-stone-950"
            onWheel={onWheelZoom}
          >
            <TemplateBlockInstanceTable
              template={template}
              blockMetas={blockMetas}
              selectedZoneId={selectedZoneId}
              onSelect={zone => setSelectedPath(zone.path)}
              onOpenPopover={openPopoverForBaked}
              showBulkUpdate={staleInstances.length > 0}
              onBulkUpdateToLatest={onBulkUpdateToLatest}
            />
            <div
              ref={canvasPaneRef}
              data-testid="td-canvas-pane"
              className="flex flex-1 items-start p-10"
              onClick={onPaneClick}
            >
              {/* 2×2 grid: corner cell (inputs + lock), horizontal ruler,
                  vertical ruler, canvas. Each ruler shares its row/column
                  with the canvas, so tick 0 and the final tick align exactly
                  with the label edges — the old flex layout offset the
                  rulers by the corner inputs, overshooting the label extent.
                  Centered via mx-auto on THIS grid, not justify-center on the
                  scroll pane above: justify-center on an overflow-auto
                  container distributes overflow symmetrically, so once the
                  grid is wider than the viewport its start (left) edge
                  becomes unreachable by scrolling. margin-auto degrades
                  gracefully instead — it collapses to 0 and the grid simply
                  starts flush left, reachable, once it no longer fits. */}
              <div
                className="mx-auto inline-grid"
                style={{ gridTemplateColumns: 'auto auto', gridTemplateRows: 'auto auto' }}
              >
                <div className="flex flex-col items-end gap-1 pb-1 pr-1">
                  <RulerDimensionInput
                    valueMm={template.widthMm}
                    orientation="horizontal"
                    disabled={dimensionsLocked}
                    onChange={onChangeWidth}
                  />
                  <div className="flex items-center gap-1">
                    <DimensionLockButton
                      locked={dimensionsLocked}
                      onToggle={onToggleDimensionsLock}
                    />
                    <RulerDimensionInput
                      valueMm={template.heightMm}
                      orientation="vertical"
                      disabled={dimensionsLocked}
                      onChange={onChangeHeight}
                    />
                  </div>
                  <PrintRotationControl
                    value={template.printRotation}
                    onChange={onChangePrintRotation}
                  />
                </div>
                <div className="self-end">
                  <Ruler
                    orientation="horizontal"
                    lengthMm={template.widthMm}
                    pxPerMm={PX_PER_MM_BASE}
                    zoom={zoom}
                  />
                </div>
                <div className="justify-self-end">
                  <Ruler
                    orientation="vertical"
                    lengthMm={template.heightMm}
                    pxPerMm={PX_PER_MM_BASE}
                    zoom={zoom}
                  />
                </div>
                <div ref={canvasContainerRef} data-testid="td-canvas" className="relative">
                  <Canvas
                    template={template}
                    pxPerMm={effectivePxPerMm}
                    selectedZoneId={selectedZoneId}
                    pickMode={Boolean(pickedBlock)}
                    onSelect={onSelect}
                    onSplit={onSplit}
                    onDrop={onZoneDrop}
                    onSplitRatioChange={onSplitRatioChange}
                    onSplitRatioDragEnd={onSplitRatioDragEnd}
                    onMergeAtPath={onMergeAtPath}
                    mode="authoring"
                    variables={{}}
                    resolvedImages={resolvedImages}
                  />
                  {!dimensionsLocked && (
                    <>
                      <div
                        data-testid="bounds-handle-right"
                        className="absolute -right-1 top-0 z-10 h-full w-2 cursor-ew-resize bg-sky-400/20 hover:bg-sky-400/40"
                        onPointerDown={e => {
                          e.currentTarget.setPointerCapture(e.pointerId)
                          setDragging('right')
                        }}
                        onPointerMove={updateDimensionsFromPointer}
                        onPointerUp={e => {
                          if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                            e.currentTarget.releasePointerCapture(e.pointerId)
                          }
                          commitDragDimensions()
                        }}
                      />
                      <div
                        data-testid="bounds-handle-bottom"
                        className="absolute -bottom-1 left-0 z-10 h-2 w-full cursor-ns-resize bg-sky-400/20 hover:bg-sky-400/40"
                        onPointerDown={e => {
                          e.currentTarget.setPointerCapture(e.pointerId)
                          setDragging('bottom')
                        }}
                        onPointerMove={updateDimensionsFromPointer}
                        onPointerUp={e => {
                          if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                            e.currentTarget.releasePointerCapture(e.pointerId)
                          }
                          commitDragDimensions()
                        }}
                      />
                      <div
                        data-testid="bounds-handle-corner"
                        className="absolute -right-1 -bottom-1 z-10 h-3 w-3 cursor-nwse-resize bg-sky-500/40 hover:bg-sky-500/60"
                        onPointerDown={e => {
                          e.currentTarget.setPointerCapture(e.pointerId)
                          setDragging('corner')
                        }}
                        onPointerMove={updateDimensionsFromPointer}
                        onPointerUp={e => {
                          if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                            e.currentTarget.releasePointerCapture(e.pointerId)
                          }
                          commitDragDimensions()
                        }}
                      />
                    </>
                  )}
                  {dropFitHint && (
                    <div
                      data-testid="drop-fit-hint"
                      aria-hidden
                      className="pointer-events-none absolute z-30 flex items-end justify-center p-1 ring-2 ring-inset ring-rose-500"
                      style={{
                        left: dropFitHint.x * effectivePxPerMm,
                        top: dropFitHint.y * effectivePxPerMm,
                        width: dropFitHint.w * effectivePxPerMm,
                        height: dropFitHint.h * effectivePxPerMm,
                      }}
                    >
                      <p className="rounded bg-rose-600 px-2 py-1 text-center text-[10px] font-medium text-white shadow-lg">
                        {/* #126: same laid-out mm as the toast — format before
                            interpolating, or i18next stringifies the residue.
                            Same rejection surface, same directional rounding:
                            ceil the need, floor the slot. */}
                        {t('templateDesigner.dropFitHint', {
                          blockWidth: formatMmCeil(dropFitHint.blockWidthMm),
                          blockHeight: formatMmCeil(dropFitHint.blockHeightMm),
                          hostWidth: formatMmFloor(dropFitHint.hostWidthMm),
                          hostHeight: formatMmFloor(dropFitHint.hostHeightMm),
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <TemplatePropertyPanel
            template={template}
            blockMetas={blockMetas}
            selectedZone={selectedZoneForPanel}
            selectedBorders={selectedBorders}
            onSetEdge={onSetEdge}
            onZoneDimensionsChange={onZoneDimensionsChange}
            onPlacementChange={onPlacementChange}
            onOpenSwapPopover={openPopoverForBaked}
            onEditValues={onPanelEditValues}
            onDetach={onPanelDetach}
            variableGroups={variableGroups}
            variablesLoading={variablesLoading}
            onVariableCommit={onVariableCommit}
            onRetryBlockDef={onRetryBlockDef}
            dimensionsLocked={dimensionsLocked}
          />
        </div>
        <PreviewPane
          template={template}
          onCollapse={previewPanel.toggle}
          collapseToggleRef={previewPanel.toggleRef}
        />
      </SplitPane>
      {pendingDrop && (
        <BlockPropsModal
          block={pendingDrop.block}
          existingProps={
            pendingDrop.existingBaked ? propsToStrings(pendingDrop.existingBaked.props) : undefined
          }
          onCancel={() => setPendingDrop(undefined)}
          onSubmit={confirmBlockDrop}
        />
      )}
      {pickerOpen && pickerCtx && (
        <VersionPickerPopover
          hostBox={pickerCtx.hostBox}
          versions={pickerCtx.versions}
          draft={
            pickerCtx.draft
              ? { widthMm: pickerCtx.draft.widthMm, heightMm: pickerCtx.draft.heightMm }
              : undefined
          }
          currentVersion={pickerCtx.baked.blockVersion}
          onSwap={onPickerSwap}
          onSwapToDraft={onPickerSwapDraft}
          onDetach={onPickerDetach}
          onCancel={() => {
            setPickerOpen(false)
            setPickerCtx(undefined)
          }}
          versionDefs={(() => {
            // Build a map of version → BlockDefinition from the already-loaded
            // blockDefCache. Versions not yet cached or errored have no entry (no diff shown).
            const map = new Map<number, BlockDefinition>()
            for (const v of pickerCtx.versions) {
              const cached = defOf(blockDefCache[`${pickerCtx.baked.blockId}@${v.version}`])
              if (cached) map.set(v.version, cached)
            }
            return map
          })()}
        />
      )}
      {toast && (
        <div
          data-testid="toast"
          className="fixed bottom-4 right-4 z-50 rounded border border-stone-300 bg-white px-3 py-2 text-xs shadow-lg dark:border-stone-700 dark:bg-stone-900"
        >
          {toast}
        </div>
      )}
    </div>
  )
}
